import React, { useCallback, useEffect, useRef, useState } from 'react';
import { throttle } from 'lodash';
import { Textarea } from '@fiverr-private/forms';
import { IconButton } from '@fiverr-private/button';
import { TrashBinIcon } from '@fiverr-private/visuals';
import { Stack, Container } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { useIsomorphicLayoutEffect } from '@fiverr-private/hooks';
import { MAX_QUESTION_LENGTH } from '../utils/constants';
import { appTranslate } from '../../../../i18n';

interface AddedQuestionInputProps {
  value: string;
  onChange: (value: string) => void;
  onDelete: () => void;

  isCounterVisible?: boolean;
  isRemoveDisabled?: boolean;
}

export const AddedQuestionInput: React.FC<AddedQuestionInputProps> = ({
  value,
  onChange,
  onDelete,
  isCounterVisible = false,
  isRemoveDisabled = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const characterCount = value.length;

  const resizeTextarea = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.overflow = 'hidden';
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, []);

  const handleChange = async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    resizeTextarea();
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  useIsomorphicLayoutEffect(() => {
    resizeTextarea();
  }, [value, isFocused, resizeTextarea]);

  useEffect(() => {
    const throttledResize = throttle(resizeTextarea, 100, { leading: true, trailing: true });
    window.addEventListener('resize', throttledResize);

    return () => {
      window.removeEventListener('resize', throttledResize);
    };
  }, [resizeTextarea]);

  return (
    <Stack direction="column" gap="1">
      <Stack direction="row" gap="2" alignItems="center">
        <Container flexGrow={1}>
          <Textarea
            rows={1}
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            maxLength={MAX_QUESTION_LENGTH}
            placeholder={appTranslate('sections.screening_questions.input_placeholder').toString()}
          />
        </Container>
        <Container
          display={{
            default: isFocused ? 'none' : 'block',
            sm: 'block',
          }}
        >
          <IconButton
            aria-label={appTranslate('sections.screening_questions.delete_question_aria_label').toString()}
            intent="secondary"
            variant="ghost"
            size="sm"
            onClick={onDelete}
            disabled={isRemoveDisabled}
          >
            <TrashBinIcon />
          </IconButton>
        </Container>
      </Stack>
      {(isFocused || isCounterVisible) && (
        <Text size="b_sm" color="bodySecondary">
          {characterCount}/{MAX_QUESTION_LENGTH}
        </Text>
      )}
    </Stack>
  );
};
